import { useState } from 'react'
import type { AlbumRecord, AppState } from '../types'
import { getThemeDefinition, themeDefinitionList } from '../data'
import { normalizeAlbumConfig } from '../../../shared/persistence'
import { useI18n } from '../i18n/context'
import { AlbumCard } from './AlbumCard'
import bookmarkDarkIcon from '../assets/icons/dark/bookmark.png'
import bookmarkLightIcon from '../assets/icons/light/bookmark.png'

const MAX_ALBUMS = 50
const MAX_ALBUM_NAME_LENGTH = 80

interface Props {
  state: AppState
  onSelectAlbum: (albumId: string) => void
  onCreateAlbum: (record: AlbumRecord) => void
  onDeleteAlbum: (albumId: string) => void
  isDarkMode: boolean
  onToggleTheme: () => void
}

export function AlbumSelector({
  state,
  onSelectAlbum,
  onCreateAlbum,
  onDeleteAlbum,
  isDarkMode,
  onToggleTheme
}: Props): JSX.Element {
  const { t } = useI18n()
  const newAlbumIcon = isDarkMode ? bookmarkDarkIcon : bookmarkLightIcon
  const availableThemes = themeDefinitionList()
  const [showForm, setShowForm] = useState(false)
  const [newName, setNewName] = useState('')
  const [newTheme, setNewTheme] = useState<AlbumRecord['config']['theme']>('standard50')
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null)

  function handleCreate(): void {
    if (!newName.trim()) return
    const now = new Date().toISOString()
    const selectedTheme = getThemeDefinition(newTheme)
    const config = normalizeAlbumConfig({
      id: crypto.randomUUID(),
      name: newName.trim(),
      size: selectedTheme.size,
      theme: newTheme,
      createdAt: now,
      updatedAt: now,
      savedInvestments: [
        {
          id: 'no-declaration',
          name: t.noDeclaration,
          kind: t.noDeclarationKind,
          notes: t.noDeclarationNotes
        }
      ],
      targetFinishDate: ''
    })
    onCreateAlbum({ config, stickers: {} })
    setNewName('')
    setNewTheme('standard50')
    setShowForm(false)
  }

  function handleDeleteConfirm(): void {
    if (deleteConfirmId) {
      onDeleteAlbum(deleteConfirmId)
      setDeleteConfirmId(null)
    }
  }

  const atMax = state.albums.length >= MAX_ALBUMS

  return (
    <div className="selector-shell">
      <div className="background-orb orb-one" />
      <div className="background-orb orb-two" />

      <header className="selector-header">
        <div className="selector-header-copy">
          <p className="eyebrow">{t.appName}</p>
          <h1>{t.yourAlbums}</h1>
          <p className="hero-copy">{t.selectAlbumDesc}</p>
        </div>
        <button
          className="ghost-button theme-toggle-button selector-theme-toggle"
          type="button"
          onClick={onToggleTheme}
          aria-label={isDarkMode ? t.lightMode : t.darkMode}
        >
          {isDarkMode ? t.lightMode : t.darkMode}
        </button>
      </header>

      <div className="album-grid">
        {state.albums.map((record) => (
          <AlbumCard
            key={record.config.id}
            record={record}
            onSelect={() => onSelectAlbum(record.config.id)}
            onDelete={() => setDeleteConfirmId(record.config.id)}
          />
        ))}

        {!atMax && (
          <button
            className="album-card album-card-new"
            onClick={() => setShowForm(true)}
            type="button"
          >
            <img src={newAlbumIcon} alt="" aria-hidden="true" className="new-album-icon" />
            <span>{t.newAlbum}</span>
          </button>
        )}

        {atMax && <p className="selector-limit">{t.maxAlbumsReached}</p>}
      </div>

      {showForm && (
        <div className="modal-backdrop" role="presentation" onClick={() => setShowForm(false)}>
          <section
            className="modal-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-heading">
              <div>
                <p className="eyebrow">{t.newAlbumTitle}</p>
                <h2>{t.configureCollection}</h2>
              </div>
            </div>

            <div className="form-grid">
              <label className="field field-wide">
                <span>{t.albumName}</span>
                <input
                  type="text"
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                  maxLength={MAX_ALBUM_NAME_LENGTH}
                  autoFocus
                  placeholder={t.albumNamePlaceholder}
                />
              </label>

              <label className="field">
                <span>{t.albumSize}</span>
                <select
                  value={newTheme}
                  onChange={(e) => setNewTheme(e.target.value as typeof newTheme)}
                >
                  {availableThemes.map((td) => (
                    <option key={td.key} value={td.key}>
                      {td.size} {t.stickersLabel}
                    </option>
                  ))}
                </select>
                <small className="field-hint">{t.availableThemes(availableThemes.length)}</small>
              </label>
            </div>

            <div className="modal-actions">
              <button className="ghost-button" onClick={() => setShowForm(false)} type="button">
                {t.cancel}
              </button>
              <button
                className="primary-button"
                onClick={handleCreate}
                type="button"
                disabled={!newName.trim()}
              >
                {t.createAlbum}
              </button>
            </div>
          </section>
        </div>
      )}

      {deleteConfirmId !== null && (
        <div
          className="modal-backdrop"
          role="presentation"
          onClick={() => setDeleteConfirmId(null)}
        >
          <section
            className="modal-card"
            role="dialog"
            aria-modal="true"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="panel-heading">
              <div>
                <p className="eyebrow">{t.confirmDeletion}</p>
                <h2>{t.deleteThisAlbum}</h2>
              </div>
            </div>
            <p className="modal-copy">{t.deleteWarning}</p>
            <div className="modal-actions">
              <button
                className="ghost-button"
                onClick={() => setDeleteConfirmId(null)}
                type="button"
              >
                {t.cancel}
              </button>
              <button className="primary-button danger" onClick={handleDeleteConfirm} type="button">
                {t.deletePermanently}
              </button>
            </div>
          </section>
        </div>
      )}
    </div>
  )
}
